'use client'

import type { RevenueByDayPoint } from '@mentora/data-access'
import type { ChartConfig } from '@mentora/ui-components'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@mentora/ui-components'
import { useFormatter, useTranslations } from 'next-intl'
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'

/**
 * Daily revenue, stacked by what was sold. `currency` is the storefront's
 * list currency (`STOREFRONT_CURRENCY`) — every amount on the dashboard is in
 * it. `showTickets` adds the event-ticket series; without it the chart is the
 * course revenue alone, labelled as plain revenue.
 */
export function RevenueChart({
  points,
  currency,
  showTickets,
}: {
  points: RevenueByDayPoint[]
  currency: string
  showTickets: boolean
}) {
  const t = useTranslations('dashboard.charts')
  const format = useFormatter()

  const chartConfig: ChartConfig = {
    courses: { label: showTickets ? t('courses') : t('revenue'), color: 'var(--chart-1)' },
    tickets: { label: t('tickets'), color: 'var(--chart-2)' },
  }

  const data = points.map(p => ({
    date: p.date,
    courses: p.courseRevenueCents / 100,
    tickets: p.ticketRevenueCents / 100,
  }))

  const money = (value: number, compact: boolean) =>
    format.number(value, {
      style: 'currency',
      currency,
      ...(compact ? { notation: 'compact', maximumFractionDigits: 1 } : {}),
    })

  const series = showTickets ? (['courses', 'tickets'] as const) : (['courses'] as const)

  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-60 w-full">
      <AreaChart data={data} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
        <defs>
          {series.map(key => (
            <linearGradient key={key} id={`revenueFill-${key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={`var(--color-${key})`} stopOpacity={0.4} />
              <stop offset="100%" stopColor={`var(--color-${key})`} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          minTickGap={32}
          tickFormatter={value =>
            format.dateTime(new Date(`${value}T12:00:00`), { day: '2-digit', month: '2-digit' })}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={64}
          tickFormatter={value => money(Number(value), true)}
        />
        <ChartTooltip
          content={(
            <ChartTooltipContent
              indicator="line"
              labelFormatter={value =>
                format.dateTime(new Date(`${String(value)}T12:00:00`), {
                  day: '2-digit',
                  month: 'long',
                })}
              formatter={(value, name) => (
                <div className="flex w-full items-center justify-between gap-4 leading-none">
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <span
                      className="size-2.5 shrink-0 rounded-[2px]"
                      style={{ backgroundColor: `var(--color-${String(name)})` }}
                    />
                    {chartConfig[String(name)]?.label}
                  </span>
                  <span className="font-mono font-medium text-foreground tabular-nums">
                    {money(Number(value), false)}
                  </span>
                </div>
              )}
            />
          )}
        />
        {series.map(key => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stackId="revenue"
            stroke={`var(--color-${key})`}
            strokeWidth={2}
            fill={`url(#revenueFill-${key})`}
          />
        ))}
      </AreaChart>
    </ChartContainer>
  )
}
